import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto.ts'
import type { DesignTemplateCategoryKey } from '@mathflat/domain/@entities/DesignTemplate/constant'
import {
  CoverData,
  type TextCoverDataItem,
} from '@mathflat/domain/@entities/DesignTemplate/CoverData/dto'
import type { DesignTemplate } from '@mathflat/domain/@entities/DesignTemplate/dto'
import { urlToFile } from '@mathflat/shared/@common/utils/file/urlToFile'
import type { WithAllKR } from '@mathflat/shared/@types/utilityTypes'
import { useEffect, useState } from 'react'
import {
  type Path,
  type PathValue,
  useForm,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form'

import { MAKE_LESSON_MATERIAL_STEP1_TYPE } from '@/dto/LessonMaterial/MakeLessonMaterialCommonParams'
import useSetRepository from '@/hooks/useSetRepository'
import commonRepo from '@/repository/common.repo'
import { DesignTemplateService } from '@/service/designTemplateController.service'
import modalService from '@/service/modal.service'

import type {
  CoverDataFieldValues,
  ExtractedCoverDataItem,
  LogoImageFieldValues,
} from '../CoverEditorTemplate'

// 앞으로 추가되는 교재/학습지 있으면 여기에 추가
type ContentT = WorkbookEntity.CustomWorkbook | WorkbookEntity.CustomSignatureWorkbook

// 초기화하는데 필요한 최소 기본 데이터
type BasicDataT<T extends 'create' | 'edit' = 'create' | 'edit'> = T extends 'create'
  ? Pick<ContentT, 'schoolType'>
  : Pick<
      ContentT,
      | 'designTemplateId'
      | 'coverData'
      | 'headtitle'
      | 'title'
      | 'subtitle'
      | 'schoolType'
      | 'maxPage'
    >
export type CoverDataOfContentT = NonNullable<ContentT['coverData']>
export type PreAssignedCoverDataOfContentT = CoverDataOfContentT['preAssignedCoverData']
export const ACADEMY_NAME_TYPE = {
  IMAGE: 'IMAGE',
  TEXT: 'TEXT',
} as const

export function useCoverDataFieldValuesForm<T extends CoverDataFieldValues>({
  fetchDesignTemplate,
  fetchDesignTemplateSummaries,
  defaultValues,
  handleLeaveProps,
  stepOneType,
}: {
  stepOneType?: ValueOf<typeof MAKE_LESSON_MATERIAL_STEP1_TYPE>
  fetchDesignTemplate: DesignTemplateService<any>['_fetchDesignTemplate']
  fetchDesignTemplateSummaries: DesignTemplateService<any>['_fetchDesignTemplateSummaries']
  defaultValues: UseFormProps<T>['defaultValues']
  handleLeaveProps?: {
    isCreate: boolean
    onPrev?: () => void
    onClose?: () => void
  }
}) {
  const designTemplateService = useSetRepository(DesignTemplateService, {
    fetchDesignTemplate,
    fetchDesignTemplateSummaries,
  })

  const methods = useForm<T>({
    defaultValues,
  })

  const [academyNameType, setAcademyNameType] = useState<ValueOf<typeof ACADEMY_NAME_TYPE>>(
    commonRepo.academy?.logoOperation === 'REPLACE'
      ? ACADEMY_NAME_TYPE.IMAGE
      : ACADEMY_NAME_TYPE.TEXT,
  )

  const getIsDirty = () => {
    return Object.keys(methods.formState.dirtyFields).length > 0
  }

  const setTemplate = async (
    designTemplateId: DesignTemplate<PreAssignedCoverDataOfContentT>['id'],
    {
      isPushRecent = false,
      category,
    }: {
      isPushRecent?: boolean
      category?: WithAllKR<DesignTemplateCategoryKey>
    } = {},
  ) => {
    const formValues = methods.getValues()
    methods.reset(formValues)

    // 1. 템플릿 가져오기 (cached)
    const currentTemplate =
      await designTemplateService.fetchAndGetDesignTemplateById(designTemplateId)

    if (!currentTemplate) {
      return
    }

    // 2. 템플릿 컬러 리스트 얻기
    designTemplateService.fetchDesignTemplateSummaries(currentTemplate.color)

    // 3. 커버데이터 전처리 : 책등영역 작업
    // 4. 커버데이터의 텍스트 value 현재 form 값으로 세팅
    const preProcessedCoverData =
      await DesignTemplateService.preProcessCoverDataAndSetCoverDataValueWithFormValue({
        coverData: currentTemplate.coverData,
        formValues,
        pageSize: currentTemplate.pageSize,
        setFormValue: (id, value) => {
          methods.setValue(id as Path<T>, value as PathValue<T, Path<T>>)
        },
      })

    // 5. 전처리한 커버데이터를 다시 currentTemplate에 세팅
    currentTemplate.setCoverData(preProcessedCoverData)

    // 6. currentTemplate을 service에 세팅
    designTemplateService.setCurrentTemplate(currentTemplate!)

    // 7. 최근 선택한 표지 템플릿에 추가
    if (isPushRecent) {
      designTemplateService.addRecentDesignTemplate(currentTemplate.toSummary())
    }

    // 8. 카테고리 세팅
    if (category) {
      designTemplateService.selectedCategory = category
    }
  }

  const setTemplateTheme = async (theme: WithAllKR<DesignTemplateCategoryKey>) => {
    designTemplateService.selectedCategory = theme
  }

  const setTemplateColor = async (color: DesignTemplate<any>['color'], pageSize?: number) => {
    if (!designTemplateService.currentDesignTemplate) {
      throw new Error('currentDesignTemplate is not set')
    }

    const summaries = await designTemplateService.fetchDesignTemplateSummaries(color)
    const defaultTemplate =
      summaries.find(({ type }) => type === designTemplateService.currentDesignTemplate?.type) ??
      summaries?.[0]

    if (!defaultTemplate) {
      throw new Error('template not found')
    }

    const template = await designTemplateService.fetchAndGetDesignTemplateById(defaultTemplate.id)
    designTemplateService.setCurrentTemplate(template)

    if (pageSize) {
      template.pageSize = pageSize
    }

    await setTemplate(template.id)
  }

  const fetchDesignTemplateDefault = async () => {
    const summaries = await designTemplateService.fetchDesignTemplateSummaries('WHITE')

    if (summaries.length === 0) {
      throw new Error('default template not found')
    }

    return await designTemplateService.fetchAndGetDesignTemplateById(summaries[0].id)
  }

  const step1GetDesignTemplateFromBasicData = async (data: BasicDataT) => {
    const currentDesignTemplate =
      'coverData' in data && 'designTemplateId' in data && data.designTemplateId // 수정/복제후수정 케이스
        ? await designTemplateService.fetchAndGetDesignTemplateById(data.designTemplateId)
        : await fetchDesignTemplateDefault()

    if (!currentDesignTemplate) {
      throw Error('currentDesignTemplate not found')
    }

    designTemplateService.fetchDesignTemplateSummaries(currentDesignTemplate.color)

    if ('maxPage' in data && data.maxPage) {
      currentDesignTemplate.pageSize = data.maxPage
    }
    if ('coverData' in data && data.coverData) {
      if (data.coverData.getPreAssignedItem('academyName')) {
        setAcademyNameType(ACADEMY_NAME_TYPE.TEXT)
      }

      // 최신 템플릿의 coverData를 유지하기 위해 최신 템플릿에 데이터 복사
      const coverData = new CoverData(currentDesignTemplate.coverData)
      data.coverData.allItems.forEach((item) => {
        if (item.id in coverData.preAssignedCoverData) {
          coverData.preAssignedCoverData[item.id] = item
        } else {
          coverData.customCoverData = coverData.customCoverData.map((_item) => {
            if (_item.id === item.id) {
              return item
            }
            return _item
          })
        }
      })

      currentDesignTemplate.setCoverData(coverData)
    }
    return { currentTemplate: currentDesignTemplate, data }
  }

  const step2UpdateFormUsingTemplateData = async ({
    currentTemplate,
    data,
  }: {
    currentTemplate: DesignTemplate<{
      [key in keyof CoverDataFieldValues]: ExtractedCoverDataItem<key>
    }>
    data: BasicDataT
  }) => {
    const isSubtitleExist = currentTemplate.coverData.getItem('subtitle')

    // 기본 정보들
    const headtitle =
      'headtitle' in data ? data.headtitle : methods.getValues('headtitle.value' as Path<T>)
    const _title =
      'title' in data ? data.title : (methods.getValues('title.value' as Path<T>) as string)

    const isCreate = !('coverData' in data && 'designTemplateId' in data && data.designTemplateId)

    const subtitle =
      'subtitle' in data ? data.subtitle : methods.getValues('subtitle.value' as Path<T>)

    const frontLogoImage =
      'coverData' in data &&
      data.coverData?.getPreAssignedItem('frontLogoImage')?.props.imgProps?.src
        ? await urlToFile(
            data.coverData!.getPreAssignedItem('frontLogoImage')!.props.imgProps!.src!,
            'frontLogoImage',
          )
        : isCreate && commonRepo.academy?.logoOperation !== 'DELETE' && commonRepo.academy?.logoUrl
          ? await urlToFile(commonRepo.academy.logoUrl, 'frontLogoImage')
          : methods.getValues('frontLogoImage' as Path<T>)

    const backLogoImage =
      'coverData' in data &&
      data.coverData?.getPreAssignedItem('backLogoImage')?.props.imgProps?.src
        ? await urlToFile(
            data.coverData!.getPreAssignedItem('backLogoImage')!.props.imgProps!.src!,
            'backLogoImage',
          )
        : isCreate && commonRepo.academy?.logoOperation !== 'DELETE' && commonRepo.academy?.logoUrl
          ? await urlToFile(commonRepo.academy.logoUrl, 'backLogoImage')
          : methods.getValues('backLogoImage' as Path<T>)

    // 생성이고, 새로운 템플릿에 subtitle이 없을 경우, title에 subtitle 추가
    const title = isCreate && !isSubtitleExist ? _title + ' - ' + subtitle : _title

    const academyNameCoverDataItem =
      'coverData' in data && data.coverData?.getPreAssignedItem('academyName')
        ? (data.coverData.getPreAssignedItem('academyName') as TextCoverDataItem<'academyName'>)
        : (currentTemplate.coverData.getItem('academyName') as TextCoverDataItem<'academyName'>)

    const academyName = isCreate
      ? commonRepo.academy?.name
      : academyNameCoverDataItem?.props.defaultValue || commonRepo.academy?.name

    const sideTitle = (() => {
      if (
        'coverData' in data &&
        data.coverData?.getPreAssignedItem('sideTitle').props.defaultValue
      ) {
        return data.coverData.getPreAssignedItem('sideTitle').props.defaultValue
      }
      return stepOneType === MAKE_LESSON_MATERIAL_STEP1_TYPE['시중교재'] ? '' : title
    })()

    methods.reset({
      ...methods.getValues(),
      headtitle: {
        value: headtitle,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('headtitle').props.renderProps.fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('headtitle').props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('headtitle').props.renderProps.maxFontSize,
      },
      subtitle: {
        value: subtitle,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('subtitle')?.props.renderProps.fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('subtitle')?.props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('subtitle')?.props.renderProps.maxFontSize,
      },
      title: {
        value: title,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('title').props.renderProps.fontFamily,
        fontSize: currentTemplate.coverData.getPreAssignedItem('title').props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('title').props.renderProps.maxFontSize,
      },
      academyName: {
        value: academyName,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('academyName')?.props.renderProps.fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('academyName')?.props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('academyName')?.props.renderProps
            .maxFontSize,
      },
      studentName: {
        value: '학생이름',
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('studentName').props.renderProps.fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('studentName').props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('studentName').props.renderProps.maxFontSize,
      },
      sideTitle: {
        value: sideTitle,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('sideTitle').props.renderProps.fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('sideTitle').props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('sideTitle').props.renderProps.maxFontSize,
      },
      backTopText: {
        value:
          'coverData' in data
            ? data.coverData?.getPreAssignedItem('backTopText').props.defaultValue ||
              currentTemplate.coverData.getPreAssignedItem('backTopText').props.defaultValue
            : currentTemplate.coverData.getPreAssignedItem('backTopText').props.defaultValue,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('backTopText').props.renderProps.fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('backTopText').props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('backTopText').props.renderProps.maxFontSize,
      },
      backBottomText: {
        value:
          'coverData' in data
            ? data.coverData?.getPreAssignedItem('backBottomText').props.defaultValue ||
              currentTemplate.coverData.getPreAssignedItem('backBottomText').props.defaultValue
            : currentTemplate.coverData.getPreAssignedItem('backBottomText').props.defaultValue,
        fontFamily:
          currentTemplate.coverData.getPreAssignedItem('backBottomText').props.renderProps
            .fontFamily,
        fontSize:
          currentTemplate.coverData.getPreAssignedItem('backBottomText').props.renderProps.fontSize,
        maxFontSize:
          currentTemplate.coverData.getPreAssignedItem('backBottomText').props.renderProps
            .maxFontSize,
      },
      ...('coverData' in data &&
        data.coverData?.getPreAssignedItem('salesPrice') && {
          salesPrice: {
            value: data.coverData?.getPreAssignedItem('salesPrice')?.props.defaultValue ?? 10000,
            fontFamily:
              currentTemplate.coverData.getPreAssignedItem('salesPrice').props.renderProps
                .fontFamily,
            fontSize:
              currentTemplate.coverData.getPreAssignedItem('salesPrice').props.renderProps.fontSize,
            maxFontSize:
              currentTemplate.coverData.getPreAssignedItem('salesPrice').props.renderProps
                .maxFontSize,
          },
        }),
      frontLogoImage,
      backLogoImage,
    })
    return { currentTemplate, data }
  }

  const setDefaultForm = (data: BasicDataT<'edit'> | BasicDataT<'create'>) => {
    return new Promise<{
      completedTemplate: DesignTemplate<any>
      initialData: BasicDataT<'edit'> | BasicDataT<'create'>
      completedFormData: T
    }>((resolve, reject) => {
      // 1. 템플릿 얻기
      step1GetDesignTemplateFromBasicData(data)
        .then(async (res) => {
          // 2. 1에서 세팅된 정보로 form 업데이트(여기서 coverData정보와 data의 정보가 모두 필요해서 먼저 coverData에 데이터 세팅한 후 그 coverData를 이용해 form 업데이트)
          step2UpdateFormUsingTemplateData(res).then(({ currentTemplate, data }) => {
            resolve({
              completedTemplate: currentTemplate,
              initialData: data,
              completedFormData: methods.getValues(),
            })
          })
        })
        .catch(reject)
    })
  }

  const handleLeave = (type: 'prev' | 'close') => {
    const isDirty = getIsDirty()

    if (isDirty) {
      modalService.openWarningModal({
        content: (
          <>
            <p>화면을 이동하면 수정된 내용이 저장되지 않습니다.</p>
            <p>다른 화면으로 이동하시겠습니까?</p>
          </>
        ),
        onClick: () => {
          if (type === 'prev') {
            handleLeaveProps?.onPrev?.()
          } else {
            handleLeaveProps?.onClose?.()
          }
          modalService.closeModal()
        },
      })
    } else {
      if (type === 'prev') {
        handleLeaveProps?.onPrev?.()
      } else {
        handleLeaveProps?.onClose?.()
      }
    }
  }

  useEffect(() => {
    if (Object.keys(methods.formState.errors).length > 0) {
      methods.trigger()
    }
  }, [designTemplateService.currentDesignTemplate])

  return {
    methods,
    designTemplateService,
    setTemplate,
    setTemplateColor,
    setTemplateTheme,
    setDefaultForm,

    handleLeave,
    academyNameType,
    setAcademyNameType,
  }
}

export const handleCoverEditorUploadImageChange =
  <T extends CoverDataFieldValues>(methods: UseFormReturn<T>, id: keyof LogoImageFieldValues) =>
  async (file: File | null) => {
    if (
      file &&
      !methods.getValues('frontLogoImage' as Path<T>) &&
      !methods.getValues('backLogoImage' as Path<T>)
    ) {
      modalService.openConfirmModal(
        {
          content: '선택한 이미지를 표지의 앞면과 뒷면에 모두 삽입할까요?',
          onClick: () => {
            methods.setValue('frontLogoImage' as Path<T>, (file ?? null) as PathValue<T, Path<T>>)
            methods.setValue('backLogoImage' as Path<T>, (file ?? null) as PathValue<T, Path<T>>)
            modalService.closeModal()
          },
          onCancel: () => {
            modalService.closeModal()
            methods.setValue(id as Path<T>, (file ?? null) as PathValue<T, Path<T>>)
          },
          actionButtonText: '네',
          cancelButtonText: '아니요',
        },
        {
          hasCloseButton: false,
          onClose: () => {
            methods.setValue(id as Path<T>, (file ?? null) as PathValue<T, Path<T>>)
            return true
          },
        },
      )
      return
    } else {
      methods.setValue(id as Path<T>, (file ?? null) as PathValue<T, Path<T>>)
    }
  }
